import { useMemo } from 'react';
import {
  AgxBodyText,
  AgxColumn,
  AgxDivider,
  AgxHeader,
  AgxRow,
  Images,
} from '@urbanx/agx-ui-components';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { formatNumber } from 'utils/formatNumber';
import NoDataImg from 'assets/images/Curiosity.svg?react';
import LeaderboardRowLoader from './LeaderboardRow/LeaderboardRowLoader';
import LeaderboardRow from './LeaderboardRow/LeaderboardRow';
import './Leaderboard.scss';
import {
  LeaderboardRankings,
  StatisticsForEntityRequestEntityType,
  StatisticsForEntityRequestTimePeriod,
} from '@urbanx/agx.rex.client';

dayjs.extend(quarterOfYear);

const currentTimePeriod = (
  timePeriod: string,
  customDateRange?: { startDate: string; endDate: string }
): string => {
  if (!timePeriod) return '';

  switch (timePeriod) {
    case StatisticsForEntityRequestTimePeriod.Month.toString():
      return '- ' + dayjs().format('MMMM YYYY');
    case StatisticsForEntityRequestTimePeriod.Quarter.toString():
      const yearQuarter = ['Jan to Mar', 'Apr to Jun', 'Jul to Sep', 'Oct to Dec'];
      const currentQuarter = dayjs().quarter();
      return '- ' + yearQuarter[currentQuarter - 1] + ' ' + dayjs().format('YYYY');
    case StatisticsForEntityRequestTimePeriod.Annual.toString():
      return '- ' + dayjs().format('YYYY');
    case StatisticsForEntityRequestTimePeriod.Previous.toString():
      // Assuming Previous means last 12 months
      const lastYear = dayjs().subtract(1, 'year').add(1, 'day');
      return '- ' + `${lastYear.format('DD MMMM YYYY')} - ${dayjs().format('DD MMMM YYYY')}`;
    case StatisticsForEntityRequestTimePeriod.Custom.toString():
      if (customDateRange && customDateRange.startDate && customDateRange.endDate) {
        const startDate = dayjs(customDateRange.startDate);
        const endDate = dayjs(customDateRange.endDate);
        return `- ${startDate.format('DD MMMM YYYY')} - ${endDate.format('DD MMMM YYYY')}`;
      }
      return '';
    default:
      return '';
  }
};

interface LeaderboardProps {
  loading: boolean;
  leaderboardStats?: LeaderboardRankings | undefined;
  timePeriod: string;
  entityType: StatisticsForEntityRequestEntityType;
  hideLeaderboard: boolean;
  customDateRange?: {
    startDate: string;
    endDate: string;
  };
}

const Leaderboard = ({
  loading,
  leaderboardStats,
  timePeriod,
  entityType,
  hideLeaderboard = false,
  customDateRange,
}: LeaderboardProps) => {
  const panelHeaderText = useMemo(() => {
    const entityTypeText =
      entityType === StatisticsForEntityRequestEntityType.IndividualOrTeam
        ? 'Performers'
        : 'Partners';

    return `Top ${entityTypeText} ${currentTimePeriod(timePeriod, customDateRange)}`;
  }, [timePeriod, entityType, customDateRange]);

  const noData = !loading && !leaderboardStats;

  const leaderboardTitle =
    entityType === StatisticsForEntityRequestEntityType.Agency ? 'Agency' : 'Agent';

  return (
    <AgxColumn extraClasses="reportLeaderboard">
      <AgxHeader size={5}>
        {loading && 'Loading leaderboard data'}
        {!loading && panelHeaderText}
      </AgxHeader>
      <div className="leaderboardGrid">
        <AgxRow fill extraClasses="card-header" spaceBetween>
          <AgxBodyText small>{leaderboardTitle}</AgxBodyText>
          <AgxBodyText small>GCI</AgxBodyText>
        </AgxRow>
        <div className={`${hideLeaderboard ? 'unlock-container' : ''}`}>
          {noData && (
            <>
              <AgxDivider />
              <AgxColumn fill centered extraLargeGap extraClasses="noDataDiv">
                <NoDataImg />
                <AgxColumn>
                  <AgxHeader size={3} centered>
                    No sales have been recorded yet
                  </AgxHeader>
                  <AgxBodyText medium centered>
                    Check back in a bit!
                  </AgxBodyText>
                </AgxColumn>
              </AgxColumn>
            </>
          )}
          {loading && [...Array(6)].map((i, idx) => <LeaderboardRowLoader key={idx} />)}
          {!loading &&
            leaderboardStats?.entityRankings &&
            leaderboardStats?.entityRankings?.length > 0 &&
            leaderboardStats?.entityRankings.map((leaderboardPosition, idx) => (
              <LeaderboardRow
                key={`row-${idx}`}
                leaderboardPosition={leaderboardPosition}
                addRingToImage={
                  entityType === StatisticsForEntityRequestEntityType.IndividualOrTeam
                }
              />
            ))}
          {hideLeaderboard && (
            <div className="unlock-overlay">
              <div className="lock-icon">
                <Images.LockOutline />
              </div>
              <div className="lock-message">Leaderboard locked until annual awards.</div>
            </div>
          )}
        </div>
      </div>
      {!loading &&
        leaderboardStats?.personalEntityRanking !== undefined &&
        leaderboardStats?.personalEntityRanking !== null &&
        (leaderboardStats.personalEntityRanking?.rank ?? 0) >= 10 &&
        (leaderboardStats.personalEntityRanking?.commission ?? 0) > 0 && (
          <AgxRow
            fill
            centered
            key={leaderboardStats.personalEntityRanking.rank}
            mediumGap
            extraClasses="leaderboard-row-hidden"
          >
            <AgxBodyText small extraClasses="leaderboard-row-hidden-text">
              {leaderboardStats.personalEntityRanking.rank}
            </AgxBodyText>

            <AgxRow fill centered extraClasses="image-agent">
              <img
                src={leaderboardStats.personalEntityRanking.photoUri || 'defaultImagePath'}
                alt="Agent"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                }}
              />
              <AgxBodyText small extraClasses="leaderboard-row-hidden-text name">
                {leaderboardStats.personalEntityRanking.name}
              </AgxBodyText>
            </AgxRow>

            <AgxBodyText small extraClasses="leaderboard-row-hidden-text sales">
              ${formatNumber(leaderboardStats.personalEntityRanking?.commission?.toString() ?? '')}
            </AgxBodyText>
          </AgxRow>
        )}
    </AgxColumn>
  );
};

export default Leaderboard;
